import { motion } from "framer-motion";
import { 
  Info, 
  Factory, 
  Package, 
  Truck, 
  Briefcase, 
  Download, 
  Upload, 
  MoreHorizontal,
  Check
} from "lucide-react";

const CATEGORY_OPTIONS = [
  { 
    value: "manufacturer", 
    label: "Manufacturer",
    icon: Factory,
  },
  { 
    value: "wholesaler", 
    label: "Wholesaler",
    icon: Package,
  },
  { 
    value: "distributor", 
    label: "Distributor",
    icon: Truck,
  },
  { 
    value: "services", 
    label: "Services",
    icon: Briefcase,
  },
  { 
    value: "importer", 
    label: "Importer",
    icon: Download,
  },
  { 
    value: "exporter", 
    label: "Exporter",
    icon: Upload,
  },
  { 
    value: "other", 
    label: "Other (Specify)",
    icon: MoreHorizontal,
  },
];

export default function Step4AdditionalInfo({ formData, setFormData, errors }) {
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCategoryToggle = (categoryValue) => {
    setFormData((prev) => {
      const currentCategories = prev.selectedCategories || [];
      const isSelected = currentCategories.includes(categoryValue);

      if (isSelected) {
        // Remove category
        const updatedCategories = currentCategories.filter((c) => c !== categoryValue);
        // If "other" is deselected, clear the other specification
        if (categoryValue === "other") {
          return {
            ...prev,
            selectedCategories: updatedCategories,
            otherCategorySpecification: "",
          };
        }
        return {
          ...prev,
          selectedCategories: updatedCategories,
        };
      } else {
        // Add category
        return {
          ...prev,
          selectedCategories: [...currentCategories, categoryValue],
        };
      }
    });
  };

  const isCategorySelected = (categoryValue) => {
    return (formData.selectedCategories || []).includes(categoryValue);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      {/* Section Title */}
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
          <Info size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Additional Information About your Company
        </h2>
      </div>

      {/* Instructions */}
      <p className="mb-6 text-sm font-medium text-gray-700">
        Please tick the category which best describes the nature of your company
      </p>

      {/* Category Options - Grid Layout */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CATEGORY_OPTIONS.map((option, index) => {
          const isSelected = isCategorySelected(option.value);
          const IconComponent = option.icon;

          return (
            <motion.div
              key={option.value}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
            >
              <button
                type="button"
                onClick={() => handleCategoryToggle(option.value)}
                className={`relative w-full p-4 text-left bg-white rounded-lg border-2 transition-all duration-300 cursor-pointer group ${
                  isSelected
                    ? "text-white bg-gradient-to-br from-orange-500 to-orange-600 border-orange-500 shadow-lg"
                    : "border-gray-200 hover:border-orange-300 hover:shadow-md"
                }`}
              >
                {/* Selection Indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-2 right-2"
                  >
                    <div className="flex justify-center items-center w-5 h-5 text-white rounded-full backdrop-blur-sm bg-white/20">
                      <Check size={12} />
                    </div>
                  </motion.div>
                )}

                {/* Icon */}
                <div
                  className={`flex justify-center items-center w-10 h-10 mb-3 rounded-lg transition-all duration-300 ${
                    isSelected
                      ? "backdrop-blur-sm bg-white/20"
                      : "bg-orange-50 group-hover:scale-110"
                  }`}
                >
                  <IconComponent
                    size={20}
                    className={
                      isSelected ? "text-white" : "text-orange-600"
                    }
                  />
                </div>

                {/* Label */}
                <h4
                  className={`text-sm font-semibold leading-tight transition-colors ${
                    isSelected ? "text-white" : "text-gray-900"
                  }`}
                >
                  {option.label}
                </h4>

                {/* Hover Effect Overlay */}
                {!isSelected && (
                  <div className="absolute inset-0 bg-gradient-to-br rounded-lg transition-all duration-300 from-orange-500/0 to-orange-500/0 group-hover:from-orange-500/5 group-hover:to-orange-500/10" />
                )}
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Other Specification Input - Full Width Below Cards */}
      {isCategorySelected("other") && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <label className="block mb-2 text-sm font-semibold text-gray-700">
            Please specify your other category{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.otherCategorySpecification || ""}
            onChange={(e) =>
              handleChange("otherCategorySpecification", e.target.value)
            }
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              errors?.otherCategorySpecification
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
            placeholder="Please specify your other category..."
          />
          {errors?.otherCategorySpecification && (
            <p className="mt-1 text-sm text-red-500">
              {errors.otherCategorySpecification}
            </p>
          )}
        </motion.div>
      )}

      {errors?.selectedCategories && (
        <p className="mt-4 text-sm text-red-500">{errors.selectedCategories}</p>
      )}
    </motion.div>
  );
}

